import { useState, type PointerEvent } from 'react'

// Single-series line chart with a hover crosshair + tooltip. Recessive axes,
// 2px line, rounded ends, markers only when the series is short (dataviz specs).
export function LineChart({
  data,
  color = 'var(--blue)',
  height = 240,
  formatX,
  yLabel = 'damages',
}: {
  data: { key: string; value: number }[]
  color?: string
  height?: number
  formatX: (key: string) => string
  yLabel?: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 720
  const H = height
  const padL = 34
  const padR = 12
  const padT = 14
  const padB = 26
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  const n = data.length
  const maxV = Math.max(1, ...data.map((d) => d.value))
  const x = (i: number) => padL + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW)
  const y = (v: number) => padT + innerH - (v / maxV) * innerH

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.value).toFixed(1)}`).join(' ')
  const areaPath =
    n > 0
      ? `${linePath} L ${x(n - 1).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${x(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`
      : ''

  // ~6 evenly spaced x tick labels
  const tickEvery = Math.max(1, Math.ceil(n / 6))
  const yTicks = [0, 0.5, 1].map((f) => Math.round(maxV * f))

  function onMove(e: PointerEvent<SVGSVGElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    if (n === 0) return
    const i = Math.round(((px - padL) / innerW) * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }

  const showMarkers = n <= 14

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* recessive y gridlines + labels */}
        {yTicks.map((t, i) => {
          const gy = y(t)
          return (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={gy} y2={gy} stroke="var(--line)" strokeWidth="1" />
              <text x={padL - 6} y={gy + 3} textAnchor="end" fontSize="10" fill="var(--gray)">
                {t}
              </text>
            </g>
          )
        })}

        {/* x labels */}
        {data.map((d, i) =>
          i % tickEvery === 0 ? (
            <text key={d.key} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="var(--gray)">
              {formatX(d.key)}
            </text>
          ) : null,
        )}

        {n > 0 && <path d={areaPath} fill="url(#area-grad)" />}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {showMarkers &&
          data.map((d, i) => (
            <circle key={d.key} cx={x(i)} cy={y(d.value)} r="3.5" fill="var(--surface)" stroke={color} strokeWidth="2" />
          ))}

        {/* hover crosshair + point */}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={padT} y2={padT + innerH} stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            <circle cx={x(hover)} cy={y(data[hover].value)} r="5" fill={color} stroke="var(--surface)" strokeWidth="2" />
          </g>
        )}
      </svg>

      {hover != null && (
        <div
          style={{
            position: 'absolute',
            left: `${(x(hover) / W) * 100}%`,
            top: 0,
            transform: 'translate(-50%, -6px)',
            pointerEvents: 'none',
            background: 'var(--ink)',
            color: '#fff',
            fontSize: 12,
            padding: '4px 8px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
          }}
        >
          <strong>{data[hover].value}</strong> {yLabel} · {formatX(data[hover].key)}
        </div>
      )}
    </div>
  )
}

// Horizontal magnitude bars (single hue). Rounded ends, 2px gap via row spacing.
// Rows with an id become clickable when onSelect is provided.
export function HBarChart({
  data,
  color = 'var(--blue)',
  empty = 'No data.',
  onSelect,
}: {
  data: { name: string; c: number; id?: string | null }[]
  color?: string
  empty?: string
  onSelect?: (id: string) => void
}) {
  if (data.length === 0) return <p style={{ color: 'var(--gray)', fontSize: 14 }}>{empty}</p>
  const max = Math.max(1, ...data.map((d) => d.c))
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {data.map((d, i) => {
        const clickable = Boolean(d.id && onSelect)
        return (
          <div key={d.id ?? `${d.name}-${i}`} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 28px', alignItems: 'center', gap: 10 }}>
            {clickable ? (
              <button
                className="link-btn"
                style={{ fontSize: 13, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={d.name}
                onClick={() => onSelect?.(d.id as string)}
              >
                {d.name}
              </button>
            ) : (
              <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.name}>
                {d.name}
              </div>
            )}
            <div style={{ background: 'var(--bg)', borderRadius: 5, height: 16, overflow: 'hidden' }}>
              <div style={{ width: `${(d.c / max) * 100}%`, height: '100%', background: color, borderRadius: 5, minWidth: 3 }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{d.c}</div>
          </div>
        )
      })}
    </div>
  )
}
